import { useMemo, useState } from 'react';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import { delay, SectionHeading, tone } from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

import {
  getMusicPresetPreview,
  MUSIC_PRESET_CATEGORIES,
  MUSIC_PRESETS,
  type MusicPreset,
  type MusicPresetCategory,
} from '@/constants/musicPresets';

import { useMusicPlayer, useMusicT, useSongIdea, useStartSong } from './kit';
import SongCard from './SongCard';
import type { MusicLandingPage } from './types';

const FIRST_ROWS = 8;

/**
 * Real songs to listen to: a 30-second preview of every template, this
 * page's sounds first. "Use this sound" starts a song with that template.
 */
export default function SoundWall({ page }: { page: MusicLandingPage }) {
  const { t } = useMusicT();
  const player = useMusicPlayer();
  const { fillStyle } = useSongIdea();
  const { start } = useStartSong(page.path);
  const reveal = useReveal<HTMLDivElement>(0.1);
  const [category, setCategory] = useState<MusicPresetCategory | 'all'>('all');
  const [expanded, setExpanded] = useState(false);

  const ordered = useMemo(() => {
    const withPreview = MUSIC_PRESETS.filter((preset) =>
      Boolean(getMusicPresetPreview(preset.id)),
    );
    const first = page.sounds
      .map((id) => withPreview.find((preset) => preset.id === id))
      .filter((preset): preset is MusicPreset => Boolean(preset));
    return [
      ...first,
      ...withPreview.filter((preset) => !page.sounds.includes(preset.id)),
    ];
  }, [page.sounds]);

  const categories = MUSIC_PRESET_CATEGORIES.filter((item) =>
    ordered.some((preset) => preset.category === item.id),
  );
  const shown =
    category === 'all'
      ? ordered
      : ordered.filter((preset) => preset.category === category);
  const visible = expanded ? shown : shown.slice(0, FIRST_ROWS);

  const use = (preset: MusicPreset) => {
    if (page.mode === 'lyrics') {
      fillStyle(preset.style);
      return;
    }
    start(
      { templateId: preset.id, instrumental: preset.instrumental },
      'template',
    );
  };

  return (
    <section id='listen' className='scroll-mt-24 py-20 sm:py-24'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='grid gap-8 lg:grid-cols-12 lg:items-end'>
          <div className='lg:col-span-7'>
            <SectionHeading
              size='md'
              title={t('Listen first.')}
              tail={t('Every track here was made with this generator.')}
              lead={t(
                'Each is a 30-second clip of a song made with one of the templates. Pick a sound you like and start your song with it.',
              )}
            />
          </div>
        </div>

        <div
          role='tablist'
          aria-label={t('Filter by style')}
          className='mt-10 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
        >
          {[{ id: 'all' as const, label: 'All' }, ...categories].map((item) => {
            const active = item.id === category;
            return (
              <button
                key={item.id}
                type='button'
                role='tab'
                aria-selected={active}
                onClick={() => {
                  setCategory(item.id);
                  setExpanded(false);
                }}
                className={clsxm(
                  'flex-none whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors',
                  active
                    ? 'bg-[#10101c] text-white dark:bg-white dark:text-[#10101c]'
                    : clsxm(
                        'bg-white ring-1 ring-[#0f0f28]/[0.08] hover:ring-[#0f0f28]/20 dark:bg-white/[0.05] dark:ring-white/10 dark:hover:ring-white/25',
                        tone.body,
                      ),
                )}
              >
                {t(item.label)}
              </button>
            );
          })}
        </div>

        <div
          ref={reveal.ref}
          data-reveal={reveal.state}
          className={clsxm(
            styles.reveal,
            'mt-8 grid grid-cols-2 gap-x-5 gap-y-9 sm:grid-cols-3 lg:grid-cols-4',
          )}
        >
          {visible.map((preset, index) => (
            <SongCard
              key={preset.id}
              preset={preset}
              player={player}
              onUse={use}
              t={t}
              useLabel={
                page.mode === 'lyrics'
                  ? t('Use this style')
                  : t('Use this sound')
              }
              className={styles.rv}
              style={delay((index % FIRST_ROWS) * 50)}
            />
          ))}
        </div>

        {shown.length > FIRST_ROWS && (
          <div className='mt-12 text-center'>
            <button
              type='button'
              onClick={() => setExpanded((value) => !value)}
              className='inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-[15px] font-semibold text-[#10101c] ring-1 ring-[#0f0f28]/10 transition hover:ring-[#0f0f28]/25 dark:bg-white/[0.06] dark:text-white dark:ring-white/15 dark:hover:ring-white/30'
            >
              {expanded
                ? t('Show fewer')
                : t('Show all {{count}} sounds', { count: shown.length })}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
